/**
 * Contacts, grouped by buying role, inline on the account and rep-firm pages.
 *
 * **Champions are visually distinct from detractors**, because a rep scanning
 * a committee is asking one question — who is on our side — and a row of
 * identical grey chips does not answer it. §9 forbids colour alone, so the
 * disposition is always a word as well.
 *
 * A contact hangs off **exactly one** of an account or a rep firm (§5.2), and
 * the two carry different write rules (§2): an account's contacts follow the
 * account, while a rep firm's channel contacts are writable by anyone who owns
 * or is the FAE on an opportunity linked to that firm. The affordance matches
 * what the API allows rather than guessing — a failed write here is a person
 * who will not try again.
 */

import { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { BUYING_ROLES, DISPOSITIONS, INFLUENCE_LEVELS } from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { humanize } from '@/lib/format';
import { cn } from '@/lib/utils';

const DISPOSITION_VARIANT: Record<string, 'success' | 'danger' | 'outline'> = {
  champion: 'success',
  detractor: 'danger',
  neutral: 'outline',
  unknown: 'outline',
};

export function ContactsPanel({
  accountId,
  repFirmId,
  canWrite,
}: {
  accountId?: string;
  repFirmId?: string;
  canWrite: boolean;
}): React.ReactElement {
  const parentParam =
    accountId !== undefined ? `accountId=${accountId}` : `repFirmId=${repFirmId ?? ''}`;
  const key = ['contacts', parentParam];
  const [editing, setEditing] = useState<Row | 'new' | null>(null);

  const contacts = useApiQuery<Paged<Row>>(key, `/api/contacts?${parentParam}&pageSize=200`);
  const items = contacts.data?.items ?? [];

  const grouped = new Map<string, Row[]>();
  for (const contact of items) {
    const role = String(contact['buying_role'] ?? 'other');
    grouped.set(role, [...(grouped.get(role) ?? []), contact]);
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Contacts</h3>
        {canWrite && (
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add contact
          </Button>
        )}
      </div>

      {repFirmId !== undefined && (
        <p className="text-xs text-muted-foreground">
          Channel salespeople. Anyone who owns or is the FAE on a deal through this firm can edit
          them — a channel contact is shared working data, and every identity change is audited.
        </p>
      )}

      {[...grouped.entries()].map(([role, people]) => (
        <div key={role}>
          <h4 className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            {humanize(role)}
          </h4>
          <ul className="grid gap-2 sm:grid-cols-2">
            {people.map((contact) => (
              <li
                key={String(contact['id'])}
                className={cn(
                  'rounded-md border p-2 text-sm',
                  contact['disposition'] === 'champion' && 'border-emerald-600/40 bg-emerald-50/60',
                  contact['disposition'] === 'detractor' && 'border-red-600/40 bg-red-50/60',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium">
                      {String(contact['first_name'])} {String(contact['last_name'])}
                      {contact['is_primary'] === true && (
                        <Badge variant="info" className="ml-1.5">
                          Primary
                        </Badge>
                      )}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {String(contact['title'] ?? '—')}
                      {contact['email'] !== null &&
                        contact['email'] !== undefined &&
                        ` · ${String(contact['email'])}`}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-1">
                      <Badge variant="outline">
                        {humanize(String(contact['influence_level']))}
                      </Badge>
                      <Badge
                        variant={DISPOSITION_VARIANT[String(contact['disposition'])] ?? 'outline'}
                      >
                        {humanize(String(contact['disposition']))}
                      </Badge>
                    </p>
                  </div>
                  {canWrite && (
                    <button
                      type="button"
                      aria-label={`Edit ${String(contact['first_name'])} ${String(contact['last_name'])}`}
                      onClick={() => setEditing(contact)}
                      className="rounded p-1 text-muted-foreground hover:bg-accent"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {items.length === 0 && (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          No contacts yet.
        </p>
      )}

      {editing !== null && (
        <ContactDialog
          contact={editing === 'new' ? null : editing}
          {...(accountId === undefined ? {} : { accountId })}
          {...(repFirmId === undefined ? {} : { repFirmId })}
          invalidate={key}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function ContactDialog({
  contact,
  accountId,
  repFirmId,
  invalidate,
  onClose,
}: {
  contact: Row | null;
  accountId?: string;
  repFirmId?: string;
  invalidate: readonly unknown[];
  onClose: () => void;
}): React.ReactElement {
  const [firstName, setFirstName] = useState(String(contact?.['first_name'] ?? ''));
  const [lastName, setLastName] = useState(String(contact?.['last_name'] ?? ''));
  const [title, setTitle] = useState(String(contact?.['title'] ?? ''));
  const [email, setEmail] = useState(String(contact?.['email'] ?? ''));
  const [role, setRole] = useState(
    String(
      contact?.['buying_role'] ?? (repFirmId === undefined ? 'design_engineer' : 'channel_sales'),
    ),
  );
  const [influence, setInfluence] = useState(String(contact?.['influence_level'] ?? 'medium'));
  const [disposition, setDisposition] = useState(String(contact?.['disposition'] ?? 'unknown'));

  const save = useApiMutation<Record<string, unknown>, void>(
    () => {
      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        title: title.trim() === '' ? null : title.trim(),
        email: email.trim() === '' ? null : email.trim(),
        buying_role: role,
        influence_level: influence,
        disposition,
        // Exactly one parent (§5.2's check constraint). Only sent on create;
        // re-parenting a contact is not a thing this screen does.
        ...(contact === null && accountId !== undefined ? { account_id: accountId } : {}),
        ...(contact === null && repFirmId !== undefined ? { rep_firm_id: repFirmId } : {}),
      };
      return contact === null
        ? { path: '/api/contacts', method: 'POST', body }
        : { path: `/api/contacts/${String(contact['id'])}`, method: 'PUT', body };
    },
    { invalidate: [invalidate], onSuccess: onClose },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={contact === null ? 'New contact' : 'Edit contact'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={firstName.trim() === '' || lastName.trim() === '' || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <Input value={firstName} onChange={(event) => setFirstName(event.target.value)} />
        </Field>
        <Field label="Last name">
          <Input value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <Field label="Email">
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Buying role">
          <Select value={role} onChange={(event) => setRole(event.target.value)}>
            {BUYING_ROLES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Influence">
          <Select value={influence} onChange={(event) => setInfluence(event.target.value)}>
            {INFLUENCE_LEVELS.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Disposition"
          hint="A subjective assessment of a named person. It is disclosable in a data-subject access request."
          className="col-span-2"
        >
          <Select value={disposition} onChange={(event) => setDisposition(event.target.value)}>
            {DISPOSITIONS.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
